import { Box, Stack, Typography } from '@mui/material';
import CellWrapper from '@common-components/tasks/columns-cells/cell-wrapper';
import OptionPickerCell from '@common-components/tasks/columns-cells/option-picker/option-picker-cell';
import {
  DBTaskColumnOptionThunks,
  DBTaskColumnThunks,
  DBTasksThunks,
  taskColumnSelectByQuery,
} from '@common-reducers/DBServiceThunks';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { ExternalLink } from 'lucide-react';
import React, { useEffect, useMemo } from 'react';
import { BoardTasksBlockData } from './board-tasks-adapter';

// Re-export the pure adapter + its types so existing importers of this module
// (btgpt-view, btgptUi/index) keep working; the logic lives in a React-free
// module so it can be unit-tested in isolation.
export {
  adaptBoardTasksResult,
  parseUrlParam,
} from './board-tasks-adapter';
export type {
  BoardTasksBlockData,
  BoardTaskItem,
  BoardCellOption,
} from './board-tasks-adapter';

// Board ids we've already dispatched hydration finds for — a module-level guard
// so remounts / multiple message blocks referencing the same board don't refetch
// (MQTT keeps the store live after the first load).
const hydratedBoardIds = new Set<string>();

const optionQuery = (rawBoardId: string, type: string) => ({
  boardId: rawBoardId,
  type,
  $or: [{ deleted: { $exists: false } }, { deleted: false }],
});

/**
 * Renders a compact, INTERACTIVE table of a Blueticks board's items under an
 * assistant message. Status/Priority cells reuse the real board `OptionPickerCell`
 * via `CellWrapper`, so clicking a pill opens the same OptionPicker popover the
 * board page uses and changing an option writes back through `updateCellValueThunk`.
 * Before the board hydrates the cells fall back to the SSE-provided `{label,bgcolor}`.
 */
export const BoardTasksMessageBlock: React.FC<BoardTasksBlockData> = ({ board, items }) => {
  const dispatch = useAppDispatch();
  const { rawBoardId } = board;

  // Hydrate this board's columns, options and tasks into the web store on first
  // sight (guarded per board id). MQTT keeps them live afterwards.
  useEffect(() => {
    if (!rawBoardId || hydratedBoardIds.has(rawBoardId)) return;
    hydratedBoardIds.add(rawBoardId);
    const query = { boardId: rawBoardId, deleted: { $ne: true }, $paginate: false } as const;
    dispatch(DBTaskColumnThunks.find({ ...query }));
    dispatch(DBTaskColumnOptionThunks.find({ ...query }));
    dispatch(DBTasksThunks.find({ ...query }));
  }, [dispatch, rawBoardId]);

  // Resolve the board's Status (status-option-picker) and Priority (option-picker)
  // column ids once the columns hydrate; null until then (cells show mockValue).
  const statusColumnId = useAppSelector((state) => {
    const [col] = taskColumnSelectByQuery(state.DBTaskColumnReducer, optionQuery(rawBoardId, 'status-option-picker'));
    return col?._id ?? null;
  });
  const priorityColumnId = useAppSelector((state) => {
    const [col] = taskColumnSelectByQuery(state.DBTaskColumnReducer, optionQuery(rawBoardId, 'option-picker'));
    return col?._id ?? null;
  });

  const openBoard = () => {
    window.open(`/tasks/?boardID=${encodeURIComponent(rawBoardId)}`, '_blank');
  };

  const cellSx = useMemo(() => ({ minWidth: 88 }), []);

  return (
    <Box
      dir="auto"
      sx={{
        mt: 2,
        border: '1px solid var(--tasks-border, #e5e7eb)',
        borderRadius: '10px',
        overflow: 'hidden',
        backgroundColor: 'var(--color-surface, #fff)',
        fontFamily: "'Inter', 'Assistant', sans-serif",
      }}
    >
      {/* Header: board name + item count + Open board */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: '1px solid var(--tasks-border, #eef0f3)',
          backgroundColor: 'var(--color-background, #f9fafb)',
        }}
      >
        <Stack direction="row" alignItems="baseline" gap={1} sx={{ minWidth: 0 }}>
          <Typography
            dir="auto"
            sx={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text, #111827)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {board.name}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'var(--color-text-secondary, #6b7280)', whiteSpace: 'nowrap' }}>
            {items.length} {items.length === 1 ? 'item' : 'items'}
          </Typography>
        </Stack>
        <button
          type="button"
          onClick={openBoard}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            border: '1px solid var(--tasks-border, #d1d5db)',
            borderRadius: 6,
            background: 'var(--color-surface, #fff)',
            color: 'var(--color-text, #374151)',
            fontSize: 12,
            fontWeight: 600,
            padding: '4px 10px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <ExternalLink size={13} />
          Open board
        </button>
      </Stack>

      {/* Rows: task text + Status cell + Priority cell */}
      {items.length === 0 ? (
        <Box sx={{ px: 1.5, py: 1.5, fontSize: 12, color: 'var(--color-text-secondary, #6b7280)' }}>
          No items
        </Box>
      ) : (
        items.map((item, idx) => (
          <Stack
            key={item.rawTaskId || idx}
            direction="row"
            alignItems="center"
            gap={1}
            sx={{
              px: 1.5,
              py: 0.75,
              borderTop: idx === 0 ? 'none' : '1px solid var(--tasks-border, #f1f2f4)',
            }}
          >
            <Typography
              dir="auto"
              sx={{
                flex: 1,
                minWidth: 0,
                fontSize: 13,
                color: 'var(--color-text, #1f2937)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={item.text}
            >
              {item.text || '—'}
            </Typography>
            <Box sx={{ flex: '0 0 auto' }}>
              <CellWrapper
                columnId={statusColumnId}
                taskId={item.rawTaskId}
                boardId={rawBoardId}
                cellComponent={OptionPickerCell}
                mockValue={item.status ?? undefined}
                sx={cellSx}
              />
            </Box>
            <Box sx={{ flex: '0 0 auto' }}>
              <CellWrapper
                columnId={priorityColumnId}
                taskId={item.rawTaskId}
                boardId={rawBoardId}
                cellComponent={OptionPickerCell}
                mockValue={item.priority ?? undefined}
                sx={cellSx}
              />
            </Box>
          </Stack>
        ))
      )}
    </Box>
  );
};

export default BoardTasksMessageBlock;
